import { printLiteral } from '#src/observation-logic/conditionPrinter.ts'
import type {
  Condition,
  ConditionLiteral,
  ObservationOperand,
  Operand,
  Operator,
} from '#src/observation-logic/conditionSchema.ts'
import { convertAnalyteQuantity } from '#src/observation-logic/units.ts'
import {
  isEmptyValue,
  type ObservationValue,
} from '#src/observation-logic/value.ts'

// Kleene three-valued logic: `unknown` when a value the condition needs is
// not available (no such observation, a variable with no context).
export type Tri = true | false | 'unknown'

export interface ConditionEnv {
  // `unavailable`: the variable's context (participant, encounter) is absent.
  variable: (id: string) => ObservationValue | null | 'unavailable'
  // `missing`: nothing in the note has the code.
  observation: (
    operand: ObservationOperand
  ) => ObservationValue | null | 'missing'
}

export function triAnd(values: Tri[]): Tri {
  if (values.includes(false)) {
    return false
  }
  return values.includes('unknown') ? 'unknown' : true
}

function triOr(values: Tri[]): Tri {
  if (values.includes(true)) {
    return true
  }
  return values.includes('unknown') ? 'unknown' : false
}

function triNot(value: Tri): Tri {
  return value === 'unknown' ? 'unknown' : !value
}

// Empty groups hold, `any` included, so a group left empty in the builder
// never hides anything. An unanswered operand fails every comparison;
// whether it is answered is asked with `is answered`.
export function evaluateCondition(
  condition: Condition,
  env: ConditionEnv
): Tri {
  switch (condition.type) {
    case 'all':
      return triAnd(
        condition.conditions.map((child) => evaluateCondition(child, env))
      )
    case 'any':
      return condition.conditions.length === 0
        ? true
        : triOr(
            condition.conditions.map((child) => evaluateCondition(child, env))
          )
    case 'not':
      return triNot(evaluateCondition(condition.condition, env))
    case 'answered': {
      const value = operandValue(condition.operand, env)
      if (value === 'unknown') {
        return 'unknown'
      }
      return !isEmptyValue(value) === condition.answered
    }
    case 'compare': {
      const value = operandValue(condition.operand, env)
      if (value === 'unknown') {
        return 'unknown'
      }
      if (!value || isEmptyValue(value)) {
        return false
      }
      return compareValues(value, condition.op, condition.value, {
        code:
          condition.operand.source === 'observation'
            ? condition.operand.code
            : undefined,
      })
    }
  }
}

function operandValue(
  operand: Operand,
  env: ConditionEnv
): ObservationValue | null | 'unknown' {
  const value =
    operand.source === 'variable'
      ? env.variable(operand.id)
      : env.observation(operand)
  return value === 'unavailable' || value === 'missing' ? 'unknown' : value
}

function ordered<T extends number | string>(a: T, b: T, op: Operator): boolean {
  switch (op) {
    case '=':
    case 'in':
      return a === b
    case '!=':
      return a !== b
    case '<':
      return a < b
    case '<=':
      return a <= b
    case '>':
      return a > b
    case '>=':
      return a >= b
  }
}

const YES = new Set(['yes', 'y', 'true'])
const NO = new Set(['no', 'n', 'false'])

// Compares an answered value with a literal. Numbers convert units (for the
// observation's code when a conversion is analyte-specific) and fail when
// they cannot; codes match by code, and by system when both carry one;
// strings match without regard to case; a date compares with a date-time by
// day. Values of different kinds do not match, except yes/no codes with
// booleans and a code's display with a string.
function compareValues(
  actual: ObservationValue,
  op: Operator,
  expected: ConditionLiteral | ConditionLiteral[],
  options: { code?: string } = {}
): boolean {
  if (Array.isArray(expected)) {
    const matches = expected.some((literal) =>
      compareOne(actual, '=', literal, options)
    )
    return op === '!=' ? !matches : matches
  }
  return compareOne(actual, op, expected, options)
}

function compareOne(
  actual: ObservationValue,
  op: Operator,
  expected: ConditionLiteral,
  options: { code?: string }
): boolean {
  const equality = op === '=' || op === '!=' || op === 'in'
  // A value of another kind, or a unit that cannot convert, does not match:
  // false for every operator but `!=`, for which it is true.
  const result = (matches: boolean): boolean =>
    op === '!=' ? !matches : matches
  switch (actual.kind) {
    case 'number': {
      if (expected.kind !== 'number') {
        return result(false)
      }
      let value = actual.value
      if (actual.unit && expected.unit && actual.unit !== expected.unit) {
        const converted = convertAnalyteQuantity(
          options.code,
          actual.value,
          actual.unit,
          expected.unit
        )
        if (converted === null) {
          return result(false)
        }
        value = converted
      }
      return ordered(value, expected.value, op)
    }
    case 'date': {
      if (expected.kind !== 'date') {
        return result(false)
      }
      const dayOnly = actual.value.length === 10 || expected.value.length === 10
      const a = dayOnly ? actual.value.slice(0, 10) : actual.value
      const b = dayOnly ? expected.value.slice(0, 10) : expected.value
      return ordered(a, b, op)
    }
    case 'string': {
      if (!equality) {
        return false
      }
      const text =
        expected.kind === 'string'
          ? expected.value
          : expected.kind === 'coding'
            ? expected.code
            : undefined
      return text === undefined
        ? result(false)
        : result(actual.value.toLowerCase() === text.toLowerCase())
    }
    case 'boolean': {
      if (!equality) {
        return false
      }
      const wanted =
        expected.kind === 'boolean'
          ? expected.value
          : expected.kind === 'coding' && YES.has(expected.code.toLowerCase())
            ? true
            : expected.kind === 'coding' && NO.has(expected.code.toLowerCase())
              ? false
              : undefined
      return wanted === undefined
        ? result(false)
        : result(actual.value === wanted)
    }
    case 'coding': {
      if (!equality) {
        return false
      }
      if (expected.kind === 'coding') {
        return result(
          actual.value.some(
            (entry) =>
              entry.code === expected.code &&
              (!entry.system ||
                !expected.system ||
                entry.system === expected.system)
          )
        )
      }
      if (expected.kind === 'string') {
        const text = expected.value.toLowerCase()
        return result(
          actual.value.some(
            (entry) =>
              entry.code.toLowerCase() === text ||
              entry.display?.toLowerCase() === text
          )
        )
      }
      if (expected.kind === 'boolean') {
        const words = expected.value ? YES : NO
        return result(
          actual.value.some((entry) => words.has(entry.code.toLowerCase()))
        )
      }
      return result(false)
    }
  }
}

const OPERATOR_TEXT: Readonly<Record<Operator, string>> = {
  '=': '=',
  '!=': '≠',
  '<': '<',
  '<=': '≤',
  '>': '>',
  '>=': '≥',
  in: 'is one of',
}

// The condition as a sentence for a chip: "Age ≥ 75", "Family history of
// cancer is answered", "(A and B) or C". `label` names an operand; the
// default is the variable id or `#code`.
export function describeCondition(
  condition: Condition,
  label: (operand: Operand) => string | undefined = () => undefined,
  // How a literal compared against an operand reads (a code as its answer's
  // display); else as written.
  literalLabel: (
    operand: Operand,
    literal: ConditionLiteral
  ) => string | undefined = () => undefined
): string {
  const literal = (operand: Operand, value: ConditionLiteral): string =>
    literalLabel(operand, value) ??
    (value.kind === 'coding' ? value.display : undefined) ??
    literalText(value)
  const name = (operand: Operand): string =>
    label(operand) ??
    (operand.source === 'variable' ? operand.id : `#${operand.code}`)
  const describe = (current: Condition, nested: boolean): string => {
    switch (current.type) {
      case 'all':
      case 'any': {
        if (current.conditions.length === 0) {
          return 'always'
        }
        const text = current.conditions
          .map((child) => describe(child, true))
          .join(current.type === 'all' ? ' and ' : ' or ')
        return nested && current.conditions.length > 1 ? `(${text})` : text
      }
      case 'not':
        return `not ${describe(current.condition, true)}`
      case 'answered':
        return `${name(current.operand)} is ${current.answered ? '' : 'not '}answered`
      case 'compare': {
        const operand = current.operand
        const value = Array.isArray(current.value)
          ? current.value.map((item) => literal(operand, item)).join(', ')
          : literal(operand, current.value)
        // An answer is "is" / "is not"; quantities keep their symbols.
        const worded =
          !Array.isArray(current.value) &&
          (current.value.kind === 'coding' || current.value.kind === 'boolean')
        const op =
          worded && current.op === '='
            ? 'is'
            : worded && current.op === '!='
              ? 'is not'
              : OPERATOR_TEXT[current.op]
        return `${name(current.operand)} ${op} ${value}`
      }
    }
  }
  return describe(condition, false)
}

function literalText(literal: ConditionLiteral): string {
  return literal.kind === 'string' ? literal.value : printLiteral(literal)
}
